import * as React from 'react';
import { MdOpenInNew } from 'react-icons/md';
import { FaLongArrowAltRight } from 'react-icons/fa';
import Link from 'components/Link';
import { FlatButton } from 'styles/elements';
import DarkPoolLevelsTable from 'components/DarkPoolLevelsTable';
import DarkPoolTable from 'components/DarkPoolTable/DarkPoolTable';
import DarkPoolOverviewTable from 'screens/TickerResearchPage/DarkPoolOverviewTable';
import { useGetDarkPoolLevelsQuery } from 'types/generated/client';
import { getCurrentETDateString } from 'screens/Calendar/utils/dateUtils';
import { DarkPoolSectionProps } from './types';
import { DARK_POOL_CONSTANTS } from './constants';

const DarkPoolSection: React.FC<DarkPoolSectionProps> = ({
  ticker,
  darkPoolTrades,
  darkPoolLoading,
  hasAccess,
  viewer,
  darkPoolByTicker,
}) => {
  const { data: levelsData } = useGetDarkPoolLevelsQuery({ variables: { ticker } });
  const levels = levelsData?.dark_pool_levels?.length ? levelsData?.dark_pool_levels : [];

  const groupedLevelsCount = React.useMemo(() => {
    const groupedByLevel: Record<string, boolean> = {};
    levels.forEach((level) => {
      const price = level.price.toFixed(2);
      groupedByLevel[price] = true;
    });
    return Object.keys(groupedByLevel).length;
  }, [levels]);

  const visibleTradeCount = Math.min(
    darkPoolTrades?.length || 0,
    DARK_POOL_CONSTANTS.MAX_VISIBLE_TRADES
  );
  const calculatedTradesHeight =
    DARK_POOL_CONSTANTS.HEADER_HEIGHT + DARK_POOL_CONSTANTS.ROW_HEIGHT * visibleTradeCount;
  const dynamicTradesHeight = Math.max(
    calculatedTradesHeight,
    DARK_POOL_CONSTANTS.MIN_TABLE_HEIGHT
  );
  const visibleLevelsCount = Math.min(groupedLevelsCount, DARK_POOL_CONSTANTS.MAX_VISIBLE_LEVELS);
  const calculatedLevelsHeight =
    DARK_POOL_CONSTANTS.LEVELS_HEADER_HEIGHT +
    DARK_POOL_CONSTANTS.LEVELS_ROW_HEIGHT * visibleLevelsCount;
  const dynamicLevelsHeight = Math.max(
    calculatedLevelsHeight,
    DARK_POOL_CONSTANTS.MIN_LEVELS_HEIGHT
  );

  const { hasTradesFromToday, mostRecentDate } = React.useMemo(() => {
    if (!darkPoolTrades || darkPoolTrades.length === 0) {
      return { hasTradesFromToday: false, mostRecentDate: null };
    }

    const today = getCurrentETDateString();
    const hasTodayTrades = darkPoolTrades.some((trade) => trade.date === today);
    const recentDate = darkPoolTrades[0]?.date;

    return { hasTradesFromToday: hasTodayTrades, mostRecentDate: recentDate };
  }, [darkPoolTrades]);

  const formattedMostRecentDate = React.useMemo(() => {
    if (!mostRecentDate) return '';
    const date = new Date(mostRecentDate + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, [mostRecentDate]);

  return (
    <section className="mb-8 md:mb-12">
      <div
        id="section-dark-pool-prints"
        className="mb-3 flex flex-col gap-2 md:mb-4 md:flex-row md:items-end"
      >
        <h2 className="mb-1 mr-2 text-2xl font-semibold md:text-3xl">{ticker} Dark Pool</h2>
        <div className="hidden md:block">
          <Link href={`/ticker-flow/${ticker}`} isExternal>
            <FlatButton className="mt-1">
              Open Ticker Research <MdOpenInNew />
            </FlatButton>
          </Link>
        </div>
      </div>
      {!hasTradesFromToday && mostRecentDate && (
        <p className="mb-3 text-sm text-gray-400">
          No trades yet today. Most recent trades from {formattedMostRecentDate}.
        </p>
      )}
      {darkPoolByTicker && (
        <div className="mb-4">
          <DarkPoolOverviewTable
            darkPoolByTicker={darkPoolByTicker}
            activeTicker={ticker}
            hideLevelsColumn={true}
          />
        </div>
      )}
      <div className="mb-1 overflow-auto" style={{ height: `${dynamicTradesHeight}px` }}>
        {!darkPoolLoading && (!darkPoolTrades || darkPoolTrades.length === 0) ? (
          <div className="flex h-full items-center justify-center text-2xl text-gray-400">
            No recent dark pool prints
          </div>
        ) : (
          <DarkPoolTable
            includeDate
            permissionTitle="Unlock Dark + Lit Pool"
            permissionDescription="Institutions hide billions in dark pools. Discover the trades and levels institutions don't want you to see."
            trades={darkPoolTrades}
            hasAccess={hasAccess}
            viewer={viewer}
            searchValue={''}
            loading={darkPoolLoading}
            watchListTickers={[]}
            isWatchListOnly={false}
            hasWatchListItems={false}
            hideHeader={true}
            hideLevelsColumn={true}
          />
        )}
      </div>
      {darkPoolTrades && darkPoolTrades.length > 0 && (
        <div className="my-2 text-sm text-gray-400">
          {DARK_POOL_CONSTANTS.MAX_VISIBLE_TRADES} most recent trades |{' '}
          <Link href={`/ticker-flow/${ticker}`} className="hover:text-gray-300">
            View all trades <FaLongArrowAltRight className="ml-2 inline" />
          </Link>
        </div>
      )}
      <div
        id="section-dark-pool-levels"
        className="mt-4 overflow-auto"
        style={{ height: `${dynamicLevelsHeight}px` }}
      >
        <DarkPoolLevelsTable ticker={ticker} />
      </div>
    </section>
  );
};

export default DarkPoolSection;
